import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Search, ChevronDown } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { UniversalCombobox } from '@/components/common/UniversalCombobox';
import { CollapsibleFilters, FilterField } from '@/components/common/CollapsibleFilters';
import type {
  ComplianceStatus,
  MatterFilters,
  MatterPhase,
  MatterResult,
} from '../types';

// Case 360° lifecycle filter (the legacy status filter is gone).
const PHASE_VALUES: MatterPhase[] = [
  'INTAKE',
  'PROVISIONAL_OPEN',
  'PLANNING',
  'EXECUTION',
  'REVIEW',
  'CLOSED',
  'SUSPENDED',
];

const PHASE_COLORS: Record<MatterPhase, string> = {
  INTAKE: 'bg-blue-500',
  PROVISIONAL_OPEN: 'bg-orange-500',
  PLANNING: 'bg-sky-500',
  EXECUTION: 'bg-amber-500',
  REVIEW: 'bg-violet-500',
  CLOSED: 'bg-gray-500',
  SUSPENDED: 'bg-slate-400',
};

const RESULT_COLORS: Record<MatterResult, string> = {
  WIN: 'bg-emerald-500',
  LOSE: 'bg-red-500',
  SETTLED: 'bg-amber-500',
  CANCELLED: 'bg-gray-500',
};

const COMPLIANCE_VALUES: ComplianceStatus[] = [
  'PENDING',
  'PASSED',
  'PASSED_WITH_CONDITIONS',
  'FAILED',
  'BYPASSED',
];

const COMPLIANCE_COLORS: Record<ComplianceStatus, string> = {
  PENDING: 'bg-yellow-400',
  PASSED: 'bg-emerald-600',
  PASSED_WITH_CONDITIONS: 'bg-lime-500',
  FAILED: 'bg-red-500',
  BYPASSED: 'bg-orange-500',
};

interface MattersFilterBarProps {
  filters: MatterFilters;
  searchValue: string;
  onSearchChange: (value: string) => void;
  onFiltersChange: <K extends keyof MatterFilters>(key: K, value: MatterFilters[K]) => void;
  activeFilterCount: number;
  onClearFilters: () => void;
  open?: boolean;
}

export function MattersFilterBar({
  filters,
  searchValue,
  onSearchChange,
  onFiltersChange,
  activeFilterCount,
  onClearFilters,
  open,
}: MattersFilterBarProps) {
  const { t } = useTranslation();

  // Scope the contract options to the selected client. `null` (not undefined) so the
  // combobox's param merge actually drops the previous client when the filter is cleared.
  const contractParams = React.useMemo(
    () => ({ clientId: filters.clientId ?? null }),
    [filters.clientId],
  );

  return (
    <CollapsibleFilters
      open={open}
      activeFilterCount={activeFilterCount}
      onClearFilters={onClearFilters}
      gridCols={4}
    >
      {/* Client */}
      <FilterField label={t('client')}>
        <UniversalCombobox
          url="/clients"
          labelKeys={['lastname', 'name', 'middlename']}
          valueKey="id"
          selectedId={filters.clientId}
          onSelectItem={(client) => {
            const clientId = client?.['id'] as number | undefined;
            if (clientId === filters.clientId) return; // hydration echo — not a user change
            onFiltersChange('clientId', clientId);
            // The selected contract may not belong to the new client — reset it.
            if (filters.contractId) onFiltersChange('contractId', undefined);
          }}
          placeholder={t('all')}
          className="w-full"
        />
      </FilterField>

      {/* Contract */}
      <FilterField label={t('contract')}>
        <UniversalCombobox
          url="/contracts"
          listUrl="/contracts/select-box"
          labelKey="name"
          valueKey="id"
          params={contractParams}
          selectedId={filters.contractId}
          onSelectItem={(contract) => onFiltersChange('contractId', contract?.['id'] as number | undefined)}
          placeholder={t('all')}
          className="w-full"
        />
      </FilterField>

      {/* Lifecycle phase (multi-select) */}
      <FilterField label={t('phase.label')}>
        <PhaseMultiSelect
          value={filters.phases}
          onChange={(v) => onFiltersChange('phases', v)}
        />
      </FilterField>

      {/* Result */}
      <FilterField label={t('result.result')}>
        <Select
          value={filters.result ?? 'none'}
          onValueChange={(val) =>
            onFiltersChange('result', val === 'none' ? undefined : (val as MatterResult))
          }
        >
          <SelectTrigger className="w-full h-10 bg-white border-[#cbd5e1] hover:border-[#94a3b8]">
            <SelectValue placeholder={t('all')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">{t('all')}</SelectItem>
            {(['WIN', 'LOSE', 'SETTLED', 'CANCELLED'] as MatterResult[]).map((r) => (
              <SelectItem key={r} value={r}>
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${RESULT_COLORS[r]}`} />
                  {t(`result.${r}`)}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FilterField>

      {/* Compliance */}
      <FilterField label={t('compliance.label')}>
        <Select
          value={filters.compliance ?? 'none'}
          onValueChange={(val) =>
            onFiltersChange(
              'compliance',
              val === 'none' ? undefined : (val as ComplianceStatus),
            )
          }
        >
          <SelectTrigger className="w-full h-10 bg-white border-[#cbd5e1] hover:border-[#94a3b8]">
            <SelectValue placeholder={t('all')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">{t('all')}</SelectItem>
            {COMPLIANCE_VALUES.map((c) => (
              <SelectItem key={c} value={c}>
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${COMPLIANCE_COLORS[c]}`} />
                  {t(`compliance.status.${c}`)}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FilterField>

      {/* Search */}
      <FilterField label={t('search')}>
        <div className="relative">
          <Input
            placeholder={t('search')}
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pr-10 h-10 bg-white border-[#cbd5e1] hover:border-[#94a3b8]"
          />
          <Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        </div>
      </FilterField>
    </CollapsibleFilters>
  );
}

/** Multi-select phase filter — reads/writes the comma-separated `phases` value. */
function PhaseMultiSelect({
  value,
  onChange,
}: {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
}) {
  const { t } = useTranslation();
  const selected = React.useMemo(
    () =>
      new Set(
        (value ?? '')
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean),
      ),
    [value],
  );

  const toggle = (phase: MatterPhase) => {
    const next = new Set(selected);
    if (next.has(phase)) next.delete(phase);
    else next.add(phase);
    // Keep the canonical phase order in the stored string.
    const arr = PHASE_VALUES.filter((p) => next.has(p));
    onChange(arr.length ? arr.join(',') : undefined);
  };

  const label =
    selected.size === 0
      ? t('all')
      : selected.size === 1
        ? t(`phase.${[...selected][0]}`)
        : t('phase.selectedCount', { count: selected.size });

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="h-10 w-full justify-between bg-white font-normal border-[#cbd5e1] hover:border-[#94a3b8]"
        >
          <span className="truncate">{label}</span>
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-1">
        {PHASE_VALUES.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => toggle(p)}
            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-slate-100"
          >
            <Checkbox checked={selected.has(p)} className="pointer-events-none" />
            <span className={`h-2 w-2 rounded-full ${PHASE_COLORS[p]}`} />
            <span className="flex-1 text-left">{t(`phase.${p}`)}</span>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}
